/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { useI18n } from "hooks/i18n/i18n";
import { AlertOutline, Lock } from "mdi-material-ui";
import { useEffect, useRef, useState } from "react";

const HOLD_DURATION_MS = 800;

export const AccountTicketDigitalHoldToCheckInButton: React.FC<{
  isChecked: boolean;
  idleLabel: string;
  checkedLabel: string;
  onComplete: () => void;
}> = ({ isChecked, idleLabel, checkedLabel, onComplete }) => {
  const { t } = useI18n();
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const isHolding = progress > 0;

  const stopHold = () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setProgress(0);
  };

  useEffect(() => stopHold, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isChecked || rafRef.current !== null) return;
    e.preventDefault();
    const start = performance.now();
    const tick = (now: number) => {
      const pct = Math.min(100, ((now - start) / HOLD_DURATION_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        stopHold();
        onComplete();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  return (
    <div css={containerStyle}>
      <div css={warningRowStyle}>
        <AlertOutline fontSize="small" style={{ color: "#fff" }} />
        <div css={warningTextStyle}>
          {t("ticketDetail.irreversibleActionWarning")}
        </div>
      </div>
      <div
        role="button"
        tabIndex={0}
        aria-disabled={isChecked}
        onPointerDown={handlePointerDown}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        css={holdButtonStyle(isChecked)}
      >
        <div
          css={progressBarStyle(isHolding)}
          style={{ width: `${progress}%` }}
        />
        <div css={labelStyle(isHolding, isChecked)}>
          {!isChecked && <Lock fontSize="small" />}
          <span>
            {isChecked
              ? checkedLabel
              : isHolding
                ? t("ticketDetail.confirmingHold")
                : idleLabel}
          </span>
        </div>
      </div>
    </div>
  );
};

const containerStyle = css({
  background: "#d8372e",
  padding: "12px",
});

const warningRowStyle = css({
  display: "flex",
  gap: "8px",
  alignItems: "center",
  marginBottom: "12px",
  padding: "0 2px",
});

const warningTextStyle = css({
  fontSize: "11px",
  color: "#fff",
  lineHeight: 1.45,
  fontWeight: 500,
});

const holdButtonStyle = (isChecked: boolean) =>
  css({
    position: "relative",
    overflow: "hidden",
    background: isChecked ? "#38A169" : "#fff",
    padding: "14px",
    textAlign: "center",
    cursor: isChecked ? "not-allowed" : "pointer",
    boxShadow: "0 2px 0 rgba(0, 0, 0, 0.2)",
    webkitTouchCallout: "none",
    userSelect: "none",
    touchAction: "manipulation",
    transition: "transform 0.08s",
    ...(isChecked
      ? {}
      : {
          "&:active": {
            transform: "scale(0.985)",
          },
        }),
  });

const progressBarStyle = (isHolding: boolean) =>
  css({
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    background: "#a32d2d",
    zIndex: 0,
    transition: isHolding ? "none" : "width 0.15s",
  });

const labelStyle = (isHolding: boolean, isChecked: boolean) =>
  css({
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 500,
    color: isChecked || isHolding ? "#fff" : "#a32d2d",
    transition: "color 0.1s",
  });
